import { TaskDefinition, TaskDefinitionSummary, ProcessingInput, Account, TaskRequest, TaskResult, DocumentContent, UserRequest, Patient, Subscription, CreateCheckoutSessionRequest, CreatePortalSessionRequest, StripeConnectedAccountResponse, ProductWithPrices, EhrPushConfig, HistoryAndPhysicalTextFields, HistoryAndPhysicalFieldEdit, HistoryAndPhysicalEdit } from '../types';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import * as iosAuth from './iosAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL + '/api';

export class ApiError extends Error {
  status: number;
  responseBody?: string;

  constructor(message: string, status: number, responseBody?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.responseBody = responseBody;
  }
}

// Platform-aware token retrieval
// On iOS: Uses secure token storage
// On Web: Will be set by the app via setWebAuthContext
let webAuthContext: any = null;
type ApiAuthMode = 'token' | 'session';
let authMode: ApiAuthMode = 'token';

// Set the web auth context from the component (web only)
export const setWebAuthContext = (context: any) => {
  webAuthContext = context;
};

export const useSessionAuth = () => {
  authMode = 'session';
};

export const useTokenAuth = () => {
  authMode = 'token';
};

// Get JWT token based on platform
const getAuthToken = async (): Promise<string | null> => {
  // On iOS, use iOS auth service
  if (Capacitor.getPlatform() === 'ios') {
    try {
      return await iosAuth.getAccessToken();
    } catch (error) {
      console.error('Error getting iOS access token:', error);
      return null;
    }
  }

  // On web, use the auth context
  if (!webAuthContext) {
    console.warn('Web auth context not set');
    return null;
  }

  try {
    // Get the access token from the web auth context
    const token = webAuthContext.user?.access_token;
    return token || null;
  } catch (error) {
    console.error('Error getting web access token:', error);
    return null;
  }
};

// Generic API request function with auth - uses Capacitor HTTP on mobile to bypass CORS
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const shouldAttachToken = authMode === 'token';
  const token = shouldAttachToken ? await getAuthToken() : null;

  if (shouldAttachToken && !token) {
    throw new Error('No auth token available');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...(shouldAttachToken && token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string>),
  };
  const credentials: RequestCredentials = authMode === 'session' ? 'include' : 'same-origin';

  // Use Capacitor HTTP on native platforms to bypass CORS
  if (Capacitor.isNativePlatform()) {
    if (authMode === 'session') {
      throw new Error('Session-based authentication is not supported on native platforms');
    }
    try {
      const response: HttpResponse = await CapacitorHttp.request({
        method: (options.method as string) || 'GET',
        url: url,
        headers: headers,
        data: options.body ? JSON.parse(options.body as string) : undefined,
      });

      if (response.status < 200 || response.status >= 300) {
        throw new ApiError(
          `API request failed: ${response.status}`,
          response.status,
          typeof response.data === 'string' ? response.data : JSON.stringify(response.data)
        );
      }

      // Handle empty responses (e.g., 204 No Content)
      if (response.status === 204 || !response.data) {
        return undefined as T;
      }

      return response.data as T;
    } catch (error) {
      console.error('Capacitor HTTP error:', error);
      throw error instanceof ApiError ? error : new ApiError('Capacitor HTTP error', 500, JSON.stringify(error));
    }
  }

  // Use standard fetch on web
  const config: RequestInit = {
    ...options,
    headers,
    credentials,
  };
  const response = await fetch(url, config);
  if (!response.ok) {
    const errorText = await response.text();
    throw new ApiError(
      `API request failed: ${response.status} ${response.statusText}`,
      response.status,
      errorText
    );
  }
  
  // Handle empty responses (e.g., 204 No Content)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  
  // Check if response has content before parsing JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    return (text || undefined) as T;
  }
  
  const result = await response.json();
  return result;
};

// API functions
export const api = {
  // Get task definitions
  getTaskDefinitions: (): Promise<TaskDefinition[]> => {
    return apiRequest<TaskDefinition[]>('/taskdefinitions');
  },

  pushToEhr: (request: { userRequestId: string; targetEhrKey?: string; pageUrl?: string }): Promise<EhrPushConfig> => {
    return apiRequest<EhrPushConfig>('/tasks/push-to-ehr', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },
  
  // Get processing context


  // Get account for the current user
  getAccount: (stripeConnectedAccountId: string, idToken?: string | null): Promise<Account> => {
    const url = `/accounts/me?stripeConnectedAccountId=${encodeURIComponent(stripeConnectedAccountId)}${idToken ? `&idToken=${encodeURIComponent(idToken)}` : ''}`;
    return apiRequest<Account>(url);
  },

  // Create account for the current user (no longer needed - handled by /me endpoint)
  // createAccount: (data: { accountType: string }) => {
  //   return apiRequest<Account>('/accounts', {
  //     method: 'POST',
  //     body: JSON.stringify(data),
  //   });
  // },



  // Execute task - now returns UserRequest
  executeTask: (taskRequest: TaskRequest, patientId: string, taskType: string): Promise<UserRequest> => {
    // Use direct processing endpoint for CMS1500_SOAP task type
    const endpoint = taskType === 'CMS1500_SOAP' ? '/tasks/process-cms1500' : '/tasks/queue-task';
    return apiRequest<UserRequest>(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        selectedTaskDefinitionId: taskRequest.selectedTaskDefinitionId,
        patientId: patientId,
        taskType: taskType,
        account: taskRequest.account,
        processingInput: taskRequest.processingInput
      }),
    });
  },

  // Validate template (new function)
  validateTemplate: (templateDocument: DocumentContent, account: Account, fieldsJson: string): Promise<TaskResult> => {
    // For now, use the same executeTask API but with a special template validation task
    const processingInput: ProcessingInput = {
      id: 'template-validation',
      sourceType: 'api',
      body: fieldsJson,
      subject: 'Template Validation',
      from: 'user@example.com',
      to: ['system@example.com'],
      attachments: [templateDocument],
      metadata: {},
    };

    const taskRequest: TaskRequest = {
      selectedTaskDefinitionId: '54a093ec-23e1-4415-bb2f-91b7c4684920', // This would be a special task for validation
      account: account,
      processingInput: processingInput,
    };

    // Execute validation task directly (ProcessingContext no longer needed)
    return apiRequest<TaskResult>('/tasks/queue-task', {
      method: 'POST',
      body: JSON.stringify({
        selectedTaskDefinitionId: taskRequest.selectedTaskDefinitionId,
        account: taskRequest.account,
        processingInput: taskRequest.processingInput
      }),
    });
  },

  // Upload template (new function)
  uploadTemplate: (templateDocument: DocumentContent, account: Account, templateName: string, jsonSchema: string): Promise<any> => {
    // First upload to S3
    return apiRequest<{ s3Key: string }>('/s3/upload-template', {
      method: 'POST',
      body: JSON.stringify({
        fileName: templateDocument.name,
        contentBase64: templateDocument.content
      }),
    }).then(async (s3Result) => {
      // Then create task definition
      const taskDefinition = {
        name: templateName,
        description: `Template uploaded by ${account.name}`,
        taskType: "DocumentPopulation",
        steps: [
          {
            id: "step1",
            name: "Extract Data",
            description: "Extract Data",
            prompt: `You are a JSON extraction engine that can only respond in JSON. You will be given a set of images or documents. Your task is to extract as much structured data as possible from the images. All values are initialized with empty strings and must remain as string type. Fields appearing to be a checkbox can have their value set with 'Yes'.Populate all fields using exact values from the document. Use null, empty string (\"\"\"\"), or appropriate defaults if data is missing. Format all dates as en-us. 

Respond with valid, unformatted, RAW JSON ONLY (no back-ticks, no fences), matching this schema exactly:
DOCUMENT_POPULATION_SCHEMA`,
            type: "dataExtraction",
            inputBindings: { DOCUMENT_POPULATION_SCHEMA: jsonSchema },
            outputVariable: "extractedJson"
          },
          {
            id: "step2",
            name: "Populate Template",
            description: "Populate Template",
            prompt: "",
            type: "documentPopulation",
            inputBindings: {
              "PdfTemplateS3Key": s3Result.s3Key,
              "extractedJson": " "
            },
            outputVariable: "AttachmentsResponse"
          }
        ]
      };

      return apiRequest<TaskDefinition>('/taskDefinitions', {
        method: 'POST',
        body: JSON.stringify(taskDefinition),
      });
    });
  },

  // Create Stripe checkout session
  createCheckoutSession: (request: CreateCheckoutSessionRequest): Promise<{ url: string }> => {
    return apiRequest<{ url: string }>('/stripe/checkout-session', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Create Stripe portal session
  createPortalSession: (request: CreatePortalSessionRequest): Promise<{ url: string }> => {
    return apiRequest<{ url: string }>('/stripe/portal-session', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Get current subscription
  getCurrentSubscription: (accountId: string): Promise<Subscription | { status: 'none' }> => {
    return apiRequest<Subscription | { status: 'none' }>(`/stripe/subscription?accountId=${encodeURIComponent(accountId)}`);
  },

  // Get Stripe connected account by ID (with products from Stripe API)
  getStripeConnectedAccount: (connectedAccountId: string): Promise<StripeConnectedAccountResponse> => {
    return apiRequest<StripeConnectedAccountResponse>(`/stripe-connected-accounts/${encodeURIComponent(connectedAccountId)}`);
  },

  // Get active product for a Stripe connected account (for checkout)
  getActiveProduct: (connectedAccountId: string): Promise<ProductWithPrices> => {
    return apiRequest<ProductWithPrices>(`/stripe-connected-accounts/${encodeURIComponent(connectedAccountId)}/active-product`);
  },

  // Get many task definitions by IDs
  getTaskDefinitionsBatch: (ids: string[]): Promise<TaskDefinitionSummary[]> => {
    return apiRequest<TaskDefinitionSummary[]>('/taskdefinitions/batch', {
      method: 'POST',
      body: JSON.stringify(ids),
    });
  },

  // Update account by id
  updateAccount: (id: string, account: Account): Promise<Account> => {
    return apiRequest<Account>(`/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(account),
    });
  },

  // Update account settings
  updateAccountSettings: async (accountId: string, settings: Record<string, any>): Promise<Account> => {
    const token = await getAuthToken();
    if (!token) throw new Error('No auth token available');
    
    const url = `${API_BASE_URL}/accounts/${accountId}/settings`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    if (Capacitor.isNativePlatform()) {
      const response: HttpResponse = await CapacitorHttp.request({
        method: 'PATCH',
        url: url,
        headers: headers,
        data: { settings },
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to update account settings: ${response.status}`);
      }

      return response.data as Account;
    }

    const response = await fetch(url, {
      method: 'PATCH',
      headers: headers,
      body: JSON.stringify({ settings }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update account settings: ${response.statusText}`);
    }

    return response.json();
  },

  // Fetch PDF as blob from S3 by key
  fetchPdfBlob: async (s3Key: string): Promise<Blob> => {
    const token = await getAuthToken();
    if (!token) throw new Error('No auth token available');
    const url = `${API_BASE_URL}/s3/pdf?key=${encodeURIComponent(s3Key)}`;
    
    if (Capacitor.isNativePlatform()) {
      // On mobile, use base64 endpoint and convert to blob
      return api.fetchPdfBase64(s3Key).then(base64 => {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: 'application/pdf' });
      });
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) throw new Error('Failed to fetch PDF');
    return await response.blob();
  },

  // Fetch PDF as base64 from S3 by key
  fetchPdfBase64: async (s3Key: string): Promise<string> => {
    const token = await getAuthToken();
    if (!token) throw new Error('No auth token available');
    const url = `${API_BASE_URL}/s3/pdf-base64?key=${encodeURIComponent(s3Key)}`;
    
    if (Capacitor.isNativePlatform()) {
      const response: HttpResponse = await CapacitorHttp.request({
        method: 'GET',
        url: url,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch PDF');
      }

      return response.data.base64;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) throw new Error('Failed to fetch PDF');
    const { base64 } = await response.json();
    return base64;
  },

  // Rename PDF fields for template settings
  renameTemplateFields: (s3Key: string, renameMap: Record<string, string>, taskDefinitionId: string): Promise<{ message: string; s3Key: string; etag: string; taskDefinitionId: string; newSchema: string }> => {
    return apiRequest<{ message: string; s3Key: string; etag: string; taskDefinitionId: string; newSchema: string }>('/pdf/template-rename-fields', {
      method: 'POST',
      body: JSON.stringify({
        s3Key,
        renameMap,
        taskDefinitionId
      }),
    });
  },

  // Save default values for a task definition
  saveDefaultValues: (taskDefinitionId: string, defaultValues: Record<string, string>): Promise<TaskDefinition> => {
    return apiRequest<TaskDefinition>(`/taskdefinitions/${taskDefinitionId}/default-values`, {
      method: 'PUT',
      body: JSON.stringify(defaultValues),
    });
  },

  // Transcribe audio using AWS Transcribe
  transcribeAudio: (audioBase64: string, languageCode: string = 'en-US'): Promise<any> => {
    return apiRequest<any>('/transcribe/transcribe-audio', {
      method: 'POST',
      body: JSON.stringify({
        audioBase64,
        languageCode
      }),
    });
  },

  // Get transcription job status
  getTranscriptionJobStatus: (jobName: string): Promise<any> => {
    return apiRequest<any>(`/transcribe/job-status/${jobName}`);
  },

  // Get transcript
  getTranscript: (jobName: string): Promise<any> => {
    return apiRequest<any>(`/transcribe/transcript/${jobName}`);
  },

  // Transcribe file upload
  transcribeFile: async (file: File): Promise<any> => {
    const token = await getAuthToken();
    if (!token) throw new Error('No auth token available');
    
    if (Capacitor.isNativePlatform()) {
      // On mobile, convert file to base64 and send as JSON
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return apiRequest<any>('/transcribe/transcribe-audio', {
        method: 'POST',
        body: JSON.stringify({
          audioBase64: base64,
          languageCode: 'en-US'
        }),
      });
    }

    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/transcribe/transcribe-file`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  },

  // Get user requests
  getUserRequests: (): Promise<UserRequest[]> => {
    return apiRequest<UserRequest[]>('/tasks/user-requests');
  },

  // Get specific user request
  getUserRequest: (id: string): Promise<UserRequest> => {
    return apiRequest<UserRequest>(`/tasks/user-requests/${id}`);
  },

  // Delete user request
  deleteUserRequest: (id: string): Promise<void> => {
    return apiRequest<void>(`/tasks/user-requests/${id}`, {
      method: 'DELETE',
    });
  },

  // Patient endpoints
  getPatients: (): Promise<Patient[]> => {
    return apiRequest<Patient[]>('/patients');
  },

  getPatient: (id: string): Promise<Patient> => {
    return apiRequest<Patient>(`/patients/${id}`);
  },

  createPatient: (name: string): Promise<Patient> => {
    return apiRequest<Patient>('/patients', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  updatePatient: (id: string, name: string): Promise<Patient> => {
    return apiRequest<Patient>(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  deletePatient: (id: string): Promise<void> => {
    return apiRequest<void>(`/patients/${id}`, {
      method: 'DELETE',
    });
  },

  // Send patient summary communication
  sendPatientSummary: (patientId: string, data: { requestId?: string; channel: 'email' | 'sms'; destination?: string; saveToPatient?: boolean; summary: string; }): Promise<{ status: string }> => {
    return apiRequest<{ status: string }>(`/patients/${patientId}/communications/patient-summary`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // HealthScribe Streaming endpoints
  startStreaming: (patientId: string, noteTemplate: string, taskDefinitionId: string, account: Account, sessionId?: string): Promise<UserRequest> => {
    return apiRequest<UserRequest>('/tasks/start-streaming', {
      method: 'POST',
      body: JSON.stringify({
        patientId,
        noteTemplate,
        taskDefinitionId,
        sessionId,
        account
      }),
    });
  },

  completeStreaming: (userRequestId: string, streamSessionId: string, transcriptText: string, clinicalNoteS3Key?: string, streamStatus: string = 'COMPLETED'): Promise<UserRequest> => {
    return apiRequest<UserRequest>('/tasks/complete-streaming', {
      method: 'POST',
      body: JSON.stringify({
        userRequestId,
        streamSessionId,
        transcriptText,
        clinicalNoteS3Key,
        streamStatus
      }),
    });
  },

  // Support request endpoints
  createSupportRequest: (accountId: string, message: string): Promise<any> => {
    return apiRequest<any>('/support', {
      method: 'POST',
      body: JSON.stringify({
        accountId,
        message
      }),
    });
  },

  // Update patient on a user request
  updateRequestPatient: (requestId: string, patientId: string): Promise<UserRequest> => {
    return apiRequest<UserRequest>(`/tasks/user-requests/${requestId}/patient`, {
      method: 'PATCH',
      body: JSON.stringify({
        patientId
      }),
    });
  },

  // Twilio Video token generation
  getVideoToken: (identity: string, roomName: string): Promise<{ token: string; roomName: string; identity: string }> => {
    return apiRequest<{ token: string; roomName: string; identity: string }>('/video/token', {
      method: 'POST',
      body: JSON.stringify({
        identity,
        roomName
      }),
    });
  },

  // Save combined SOAP note edit (single text block)
  saveCombinedSoapNoteEdit: (userRequestId: string, editedCombinedSoapNote: string): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>(`/tasks/user-requests/${userRequestId}/combined-soap-note-edit`, {
      method: 'POST',
      body: JSON.stringify({
        editedCombinedSoapNote
      }),
    });
  },

  // Save History & Physical edit
  saveHistoryAndPhysicalEdit: (userRequestId: string, editedFields: HistoryAndPhysicalTextFields, edits: HistoryAndPhysicalFieldEdit[]): Promise<HistoryAndPhysicalEdit> => {
    return apiRequest<HistoryAndPhysicalEdit>(`/tasks/user-requests/${userRequestId}/history-physical-edit`, {
      method: 'POST',
      body: JSON.stringify({
        editedFields,
        edits
      }),
    });
  },
};

// Auth utilities
export const auth = {
  getToken: getAuthToken
}; 